import { RightHandFinger, RightHandPart } from './types';
import { BASE64_ASSETS } from './src/generated/base64Assets';

// ============================================================================
// RIGHT HAND PICKER CONSTANTS
// ============================================================================
// This file contains all configuration for the right hand picker component.
// Based on the left hand picker but adapted for right hand key mapping and assets.

// Image URLs - These point to the actual PNG files in the assets folder
export const RIGHT_IMAGE_URLS = {
  KEYBOARD_BASE: BASE64_ASSETS.Keyboard_Base_Right,
  RIGHT_PALM_INACTIVE: BASE64_ASSETS.Right_palm_inactive,
  RIGHT_PALM_ACTIVE: BASE64_ASSETS.Right_palm_active,
  RIGHT_PINKY_ACTIVE: BASE64_ASSETS.Right_pinky_active,
  RIGHT_RING_ACTIVE: BASE64_ASSETS.Right_ring_active,
  RIGHT_MIDDLE_ACTIVE: BASE64_ASSETS.Right_middle_active,
  RIGHT_INDEX_ACTIVE: BASE64_ASSETS.Right_index_active,
  RIGHT_THUMB_ACTIVE: BASE64_ASSETS.Right_thumb_active,
  KEYS: {
    P: BASE64_ASSETS.P,
    O: BASE64_ASSETS.O,
    L: BASE64_ASSETS.L,
    I: BASE64_ASSETS.I,
    K: BASE64_ASSETS.K,
    Y: BASE64_ASSETS.Y,
    U: BASE64_ASSETS.U,
    J: BASE64_ASSETS.J,
    H: BASE64_ASSETS.H,
    B: BASE64_ASSETS.B,
    N: BASE64_ASSETS.N,
    M: BASE64_ASSETS.M,
    SPACE: BASE64_ASSETS.Space_right,
  },
};


// ============================================================================
// FINGER-KEY MAPPING - RIGHT HAND SPECIFIC
// ============================================================================
// Right hand key mapping as specified:
// Pinky: P, O, L
// Ring: I, K  
// Middle: Y, U, J
// Index: H, B, N
// Thumb: M, Space
export const RIGHT_FINGER_KEY_MAP: Record<RightHandFinger, string[]> = {
  'rightPinky': ['P', 'O', 'L'],              // Right pinky keys
  'rightRing': ['I', 'K'],                    // Right ring keys  
  'rightMiddle': ['Y', 'U', 'J'],             // Right middle keys
  'rightPointing': ['H', 'B', 'N'],           // Right index keys (rightPointing)
  'rightThumb': ['M', 'SPACE'],               // Right thumb keys
};

// Create a reverse map for efficient lookup
export const RIGHT_KEY_FINGER_MAP: Record<string, RightHandFinger> = {};
Object.entries(RIGHT_FINGER_KEY_MAP).forEach(([finger, keys]) => {
  keys.forEach(key => {
    RIGHT_KEY_FINGER_MAP[key] = finger as RightHandFinger;
  });
});

// Logging to prove compatibility with existing system
console.log('🔗 RightHandPicker: Using KeyboardHero-compatible finger-key mapping:', RIGHT_FINGER_KEY_MAP);

// ============================================================================
// SVG FINGER DETECTION SYSTEM - RIGHT HAND
// ============================================================================
// This system uses precise SVG paths extracted from Adobe Illustrator files
// for the right hand, with positioning adjustments to match finger positions.

// DEBUG MODE: Set to true to see finger areas visually (red semi-transparent overlay)
export const RIGHT_DEBUG_FINGER_AREAS = false;

// POSITIONING ADJUSTMENTS FOR RIGHT HAND - Based on 1138x1159 dimensions
export const RIGHT_FINGER_POSITION_ADJUSTMENTS: Record<RightHandPart, {
  translateX: number;
  translateY: number;
  scaleX?: number;
  scaleY?: number;
}> = {
  // RIGHT PINKY FINGER ADJUSTMENT
  [RightHandPart.PINKY]: {
    translateX: 0,    // Move LEFT (-) or RIGHT (+)
    translateY: 0,    // Move UP (-) or DOWN (+)
    scaleX: 1,        // Make narrower (0.9) or wider (1.1)
    scaleY: 1,        // Make shorter (0.9) or taller (1.1)
  },
  
  // RIGHT RING FINGER ADJUSTMENT
  [RightHandPart.RING]: {
    translateX: 0,    
    translateY: 0,    
    scaleX: 1,        
    scaleY: 1,        
  },
  
  // RIGHT MIDDLE FINGER ADJUSTMENT
  [RightHandPart.MIDDLE]: {
    translateX: 0,    
    translateY: 0,    
    scaleX: 1,        
    scaleY: 1,        
  },
  
  // RIGHT INDEX FINGER ADJUSTMENT
  [RightHandPart.INDEX]: {
    translateX: 0,    
    translateY: 0,    
    scaleX: 1,        
    scaleY: 1,        
  },
  
  // RIGHT THUMB ADJUSTMENT
  [RightHandPart.THUMB]: {
    translateX: 0,    
    translateY: 0,    
    scaleX: 1,        
    scaleY: 1,        
  },
  
  // RIGHT PALM ADJUSTMENT
  [RightHandPart.PALM]: {
    translateX: 0,    
    translateY: 0,    
    scaleX: 1,        
    scaleY: 1,        
  },
};

// SVG PATHS - Extracted from right hand Adobe Illustrator SVG files
// These represent the exact finger shapes for the right hand
export const RIGHT_HAND_PART_SVG_PATHS: Record<RightHandPart, string> = {
  // RIGHT INDEX - Extracted from Right_index_svg.svg
  [RightHandPart.INDEX]: "M368.2,463.7c-19.3,15.7-37.5,30.4-56.5,45.8c-4.4-7-9-13.6-12.9-20.7c-16.1-29.4-32.6-58.7-47.6-88.7c-7.3-14.7-18.8-25.1-29.8-36.3c-27.4-27.6-55.5-54.6-82-83.1c-16.4-17.7-29.9-38-34.8-62.4c-2.1-10.8-4.5-22,3.7-32c6-7.3,8.6-8,15.8-1.9c16.2,13.8,31.1,28.8,45.1,45c13.1,15.2,28,28.9,42.9,42.3c26.3,23.7,53,47.1,80.2,69.7c8.2,6.8,12.7,15,17,23.9c12.7,26.8,28.3,51.9,45.2,76.2C359.3,448.7,363.5,456.2,368.2,463.7z",
  
  // RIGHT PINKY - Extracted from Right_pinky_svg.svg
  [RightHandPart.PINKY]: "M558.1,484.1c-17.5-18.1-38.6-30.5-58-45c-4.1-3.1-5-5.6-3.2-10.3c7.3-19.2,14.5-38.6,21.5-58c1-2.9,1-6.5,0.3-9.5c-8.7-36.4-18.1-72.6-26.1-109c-3.5-16-1.4-32.1,5.8-47.2c4-8.4,10.4-14.1,18.6-18c7.9-3.7,14.2-0.8,15.5,7.5c2.6,16.9,4.7,33.9,7.2,50.7c0.5,3.3,2.5,6.3,3.4,9.6c8.5,30.8,16.4,61.7,25.8,92.2c4.7,15.3,5,30,2.5,45.6c-4.8,29.7-8.6,59.6-12.8,89.4C558.5,482.6,558.4,483.1,558.1,484.1z",
  
  // RIGHT RING - Extracted from Right_ring_svg.svg
  [RightHandPart.RING]: "M461.6,437.4c-8-0.4-16-1-23.9-1.2c-2.7-0.1-4.1-1-5.1-3.6c-7.6-20.6-16.6-40.8-22.9-61.8c-5.4-18.1-8.1-36.9-11.7-55.5c-1.2-6.1-2.4-11.9-5.6-17.6c-17.6-31.7-34.8-63.6-51.9-95.5c-7.9-14.6-16.2-29.1-23-44.2c-9.6-21.1-14.8-43.4-12.8-66.9c0.8-8.7,3-17,7.7-24.5c5.6-8.9,13-9.3,18.3-0.1c10.5,18.5,21.3,36.9,30,56.2c6.4,14.2,15.2,26.6,23.1,39.7c22.6,37.8,45.6,75.4,69.2,112.7c7.9,12.5,10,26.3,13.2,39.9c8.2,35.1,16.1,70.4,24,105.6c0.6,2.8,1.2,5.5,1.8,8.3c1.4,6.5-1.5,10-8.4,9.8C476.2,438.3,468.9,437.8,461.6,437.4C461.6,437.4,461.6,437.4,461.6,437.4z",
  
  // RIGHT MIDDLE - Extracted from Right_middle_svg.svg
  [RightHandPart.MIDDLE]: "M181.2,84.1c1.3-5.2,2.3-13.1,5.3-20.2c4.9-11.7,13.8-13.3,22-3.8c9.7,11.4,18.4,23.6,26.9,35.9c6.3,9.1,11,19.3,17.1,28.6c8,12.1,16.3,23.8,25,35.4c24.8,33.2,49.7,66.3,74.7,99.3c5.4,7.2,12.1,13.4,16.9,20.9c5,7.7,9,16.2,12.5,24.8c8.4,20.6,15,42.1,24.8,62c10.6,21.5,17.2,44.3,26.4,66.2c0.5,1.2,0.3,3.6-0.5,4.3c-11.8,10.2-22.1,22.4-37.1,28.2c-6.4,2.5-12.8,4.7-19.3,6.6c-1.1,0.3-3.3-0.9-4.2-2.1c-5-6.3-7.9-13.5-10.2-21.3c-4.2-14.2-9.8-27.9-15-41.7c-6.5-17.4-13.2-34.7-19.8-52.1c-3.3-8.5-6.3-17.2-9.9-25.6c-1.9-4.3-4.6-8.3-7.2-12.2c-13.6-20.3-27.4-40.5-41.1-60.7c-17.2-25.5-34.7-50.8-51.7-76.4c-15.4-23.3-28.5-47.7-33.4-75.7C182.4,98.6,182.1,92.6,181.2,84.1z",
  
  // RIGHT THUMB - Extracted from Right_thumb_svg.svg
  [RightHandPart.THUMB]: "M122.5,540c6.9-7,14.4-12.1,23.7-14.6c19.9-5.4,38.7-1.4,57.2,6.4c19.9,8.4,34.5,23,48.7,38.6c13.8,15.3,28.1,30.2,46.3,40.6c14.7,8.4,30.4,12.7,47.2,14.6c8.3,0.9,17,3.2,24,7.4c4.3,2.6,6.5,9.7,8.1,15.2c2.2,7.6-0.2,15.6-3.7,22.2c-7.3,13.9-14,28.3-24.7,40.3c-5.6,6.2-12.4,10-19.7,13.1c-18.1,7.7-13.6,6.8-25.2-5.5c-16.6-17.7-32.4-36.2-49.7-53.3c-11.9-11.8-25.7-21.6-38.4-32.5c-13.3-11.4-25.4-23.9-32.5-40.4c-1.4-3.2-4-6-6.6-8.5C160.7,567.4,143.5,551.7,122.5,540z",
  
  // RIGHT PALM - Extracted from Right_palm_svg.svg
  [RightHandPart.PALM]: "M314.2,729.1c20.8-5,36.5-15.5,46.1-34.3c5.8-11.4,13.5-21.7,16.5-34.5c2.4-10-0.5-18.3-5.8-26.3c-0.4-0.7-1.2-1.3-1.9-1.5c-8.2-2.4-16.4-5.2-24.8-7c-3.6-0.8-5.3-1.6-6.2-5.4c-8.5-35.9-17.2-71.8-25.7-107.7c-0.4-1.5,0-4.1,1.1-5c17.6-14.5,35.4-28.8,53.5-43.5c2,2.8,3.9,5.4,6,8.2c16.4-4.3,32-10,44.3-22.3c1.9-1.9,4.3-3.3,5.9-5.4c6.4-8.2,14.6-10,24.6-8.8c10.2,1.3,20.6,1.5,30.9,1.7c4.4,0.1,8.9-1.1,13.3-1.7c0.2-0.7,0.3-1.5,0.5-2.2c16,11.6,32,23.1,47.9,34.7c3.6,2.6,6.7,6,10.3,8.6c7.4,5.3,8.4,13.3,9.7,21.3c0.5,3.1,0.7,6.5,2.2,9.1c6.9,12.2,9.3,25.7,13.1,38.9c4.2,14.3,8.9,28.4,13.2,42.7c4.2,14,8.1,28,12.1,42c2.4,8.4,4.8,16.9,7.6,25.2c0.6,1.8,3.2,2.8,4.5,4.6c3.3,4.5,6.5,9.2,9.6,13.9c10.3,16.2,20.9,32.3,30.6,48.9c6.3,10.6,11.2,22.1,17.1,33c6.3,11.5,9.7,24.3,17.9,35.1c4.3,5.6,7.3,12.2,13.4,16.3c5.3,3.6,6.5,9.6,8.3,14.9c4.4,12.8,8.2,25.9,12.4,38.8c3.1,9.5,6.4,18.8,9.6,28.2c2.7,8.2,4.5,16.8,8.4,24.4c3.7,7.3,6.2,15.9,14.7,19.9c0.9,0.4,1.5,1.7,2,2.7c9.3,15.7,18.6,31.3,27.9,47c4.1,7,8,14.1,12.3,21c1.8,2.9-0.1,4.2-1.7,5.8c-22.1,22.4-44.2,44.8-66.3,67.2c-11,11.1-21.9,22.3-33,33.4c-1.5,1.5-3.7,3-5.8,3.4c-49.3,8-98.6,15.8-147.9,23.7c-0.8,0.1-1.6,0.1-3.8,0.2c-3.1-16.8-6.6-33.5-9-50.4c-4.9-33.6-9.1-67.3-13.8-100.9c-3.6-25.7-7-51.4-11.7-77c-1.8-9.8-6.5-19.1-10.6-28.3c-2.8-6.5-6.6-12.7-10.3-18.8c-1-1.7-3.4-3-5.5-3.6c-12.8-3.6-25.7-6.8-38.5-10.4c-2.6-0.7-5.3-2.1-7.2-4c-36.4-35.7-72.6-71.5-108.9-107.3C320.6,735.1,317.7,732.4,314.2,729.1z",
};

// ============================================================================
// LETTER POSITIONING & FONT SYSTEM - RIGHT HAND
// ============================================================================
// Letter positioning for right hand keyboard (1138x1159 dimensions)

// DEBUG MODE: Set to true to see letter positioning guides
export const RIGHT_DEBUG_LETTER_POSITIONS = false;

// FONT SETTINGS - Same as left hand for consistency
export const RIGHT_LETTER_FONT_SETTINGS = {
  fontSize: '22px',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif',
  fontWeight: 'bold',
  color: '#000000ff',
  textShadow: '0px 0px 0px rgba(0,0,0,0.5)',
  letterSpacing: '0px',
};

// LETTER POSITION ADJUSTMENTS - RIGHT HAND KEYBOARD
// Note: These coordinates need to be manually adjusted to match the right hand keyboard layout
export const RIGHT_LETTER_POSITION_ADJUSTMENTS: Record<string, {
  top: string;
  left: string;
  offsetX?: string;
  offsetY?: string;
}> = {
  // ROW 1 KEYS - Right side (Y, U, I, O, P)
  Y: { top: '15.1%', left: '8.7%' },
  U: { top: '15.1%', left: '18.7%' },
  I: { top: '15.1%', left: '29.2%' },
  O: { top: '15.1%', left: '40.5%' },
  P: { top: '15.1%', left: '50.9%' },
  
  // ROW 2 KEYS - Right side (H, J, K, L)
  H: { top: '25.5%', left: '13.7%' },
  J: { top: '25.5%', left: '24%' },
  K: { top: '25.5%', left: '34%' },
  L: { top: '25.5%', left: '44.5%' },
  
  // ROW 3 KEYS - Right side (N, M)
  N: { top: '35.1%', left: '16.6%' },
  M: { top: '35.1%', left: '27%' },
  
  // ROW 4 KEYS - Right side (B)
  B: { top: '35.1%', left: '6.1%' },
  
  // SPACEBAR - Right portion
  SPACE: { top: '45.5%', left: '10%' },
};

// LEGACY SUPPORT - Generated from RIGHT_LETTER_POSITION_ADJUSTMENTS
export const RIGHT_KEY_LETTER_POSITIONS: Record<string, { top: string; left: string }> = 
  Object.fromEntries(
    Object.entries(RIGHT_LETTER_POSITION_ADJUSTMENTS).map(([key, pos]) => [
      key,
      { top: pos.top, left: pos.left }
    ])
  );

// ============================================================================
// INTEGRATION HELPERS
// ============================================================================

// Helper function to get right hand finger from FingerType
export const getRightHandFingers = (fingers: string[]): RightHandFinger[] => {
  return fingers.filter(f => f.startsWith('right')) as RightHandFinger[];
};

// Helper function to convert right hand fingers to FingerType format
export const rightHandFingersToFingerType = (fingers: RightHandFinger[]): string[] => {
  return fingers.map(f => f as string);
};

console.log('✅ RightHandPicker: Constants loaded successfully!');
console.log('🎯 RightHandPicker: Ready for integration with KeyboardHero system');
